import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  Radio,
  Typography,
  // FormLabel,
  RadioGroup,
  FormControlLabel,
  Divider,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import defaultErrorToast from 'utils/toastify/defaultErrorToast';
import servipag from './servipag.png';
import PaymentSkeleton from './PaymentSkeleton';

/**
 * Reports a failed confirm request to Sentry. Only non sensitive identifiers are attached:
 * no client name, RUT or e-mail ever leaves the page.
 */
const reportPaymentError = (error, clientId, debt) =>
  Sentry.captureException(error, {
    tags: { feature: 'payment-confirm' },
    extra: { clientId, debt, status: error?.response?.status ?? null },
  });

/**
 * A 4xx is deterministic: the same body will keep being rejected, so asking the user to retry
 * in a few minutes is wrong. Only 5xx and network level failures are worth retrying. The API's
 * validation messages are internal English strings, so they are reported to Sentry rather than
 * shown here.
 */
const paymentErrorMessage = (error) => {
  const status = error?.response?.status;

  if (status >= 400 && status < 500) {
    return 'No pudimos procesar el pago con el monto seleccionado. Por favor contáctanos para completarlo.';
  }

  return 'No pudimos iniciar el pago. Intenta nuevamente en unos minutos.';
};

// eslint-disable-next-line complexity
function Payment() {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [debt, setDebt] = useState(null);
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // const [paymentPayload, setPaymentPayload] = useState();
  const [debtToPay, setDebtToPay] = useState({
    debt: '',
    total: '',
  });

  const formatNumber = (num) => num?.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

  const navigate = useNavigate();
  useEffect(() => {
    // eslint-disable-next-line complexity
    const fetchClientDebt = async () => {
      if (!clientId) {
        setLoading(false);
        navigate('/*');
        return;
      }
      try {
        const response = await fetch(`${window.location.origin}/api/v1/account-status/${clientId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch');
        }
        const { total, expired, name } = await response.json();
        setDebt({
          total: total > 0 ? formatNumber(total) : 0,
          expired: expired > 0 ? formatNumber(expired) : 0,
          toExpire: total - expired,
          rawTotal: total > 0 ? total : 0,
          rawExpired: expired > 0 ? expired : 0,
        });

        setClientName(name);
      } catch (e) {
        navigate('/*');
      }
      setLoading(false);
    };
    setLoading(true);
    fetchClientDebt();
  }, []);

  const url = `${window.location.origin}/api/v1/confirm/${clientId}`;

  // eslint-disable-next-line complexity
  const handlePayment = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const response = await axios.post(url, { debtToPay });

      const xml = response?.data?.xml1Response?.xml1;
      if (!xml) {
        throw new Error('Missing xml1 in payment response');
      }

      const form = document.createElement('form');
      form.method = 'post';
      form.action = 'https://www.servipag.com/BotonPago/BotonPago/Pagar';

      // Crear un campo de texto oculto para el XML
      const xmlInput = document.createElement('textarea');
      xmlInput.name = 'xml';
      xmlInput.value = xml;
      xmlInput.style.display = 'none';

      // Agregar el campo de texto al formulario
      form.appendChild(xmlInput);

      // Agregar el formulario al cuerpo del documento
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      // Leaving this unhandled made the button fail silently (Sentry REACT-PORTAL-DE-PAGO-3F).
      // Now that the request lives inside a try block the automatic unhandled rejection capture
      // is gone, so report it explicitly: a 400 from ConfirmPaymentDto never reaches Sentry from
      // the API side (HttpExceptionFilter only forwards 500s), and network level failures never
      // reach Nest at all. Only non sensitive identifiers go into the event.
      reportPaymentError(error, clientId, debtToPay.debt);
      setSubmitting(false);
      defaultErrorToast({ message: paymentErrorMessage(error) });
    }
  };

  const handlePaymentObj = (event) => {
    let total = 0;
    if (event.target.value === 'total') total = debt.rawTotal;
    if (event.target.value === 'expired') total = debt.rawExpired;

    setDebtToPay({
      debt: event.target.value,
      total,
    });
  };

  if (loading || !debt) {
    return <PaymentSkeleton />;
  }

  return (
    <Grid container sx={{ m: { xs: 2 } }}>
      <Grid item sx={{ margin: '24px 0' }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: '#012D55',
          }}
          variant="h4"
          fontSize={36}>
          Botón de pago
        </Typography>
      </Grid>
      <Grid item container spacing={7}>
        <Grid flexDirection="column" container item xs={12} md={6} lg={8}>
          <Grid item container>
            <Grid item sx={{ margin: '0px 0px 24px 0px' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#012D55',
                }}
                variant="h5"
                fontSize={24}>
                Seleccione monto a pagar:
              </Typography>
            </Grid>
            <Grid item container justifyContent="space-between" fullWidth>
              <FormControl fullWidth>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  onChange={handlePaymentObj}
                  value={debtToPay.debt}
                  name="radio-buttons-group">
                  <Grid item>
                    <Card
                      sx={{
                        marginBottom: '24px',
                        boxShadow: '0 5px 25px 0 rgb(175 175 175 / 30%)',
                        padding: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      variant="outlined">
                      <Box display="flex" alignItems="center" sx={{ height: '36px' }}>
                        <FormControlLabel
                          value="total"
                          control={
                            <Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: '#E10600',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontWeight: 600,
                              }}
                              fontSize={16}>
                              Deuda total
                            </Typography>
                          }
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#012D55',
                        }}
                        fontSize={16}>
                        ${debt.total}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card
                      sx={{
                        marginBottom: '32px',
                        boxShadow: '0 5px 25px 0 rgb(175 175 175 / 30%)',
                        padding: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                      variant="outlined">
                      <Box display="flex" alignItems="center" sx={{ height: '36px' }}>
                        <FormControlLabel
                          value="expired"
                          control={
                            <Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: '#E10600',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontWeight: 600,
                              }}
                              fontSize={16}>
                              Deuda vencida
                            </Typography>
                          }
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#012D55',
                        }}
                        fontSize={16}>
                        ${debt.expired}
                      </Typography>
                    </Card>
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          <Grid item>
            <Typography
              sx={{
                fontWeight: 700,
                color: '#012D55',
              }}
              variant="h5"
              fontSize={24}>
              Método de pago
            </Typography>
            <Card
              sx={{
                margin: '24px 0px',
                boxShadow: '0 5px 25px 0 rgb(175 175 175 / 30%)',
                // padding: 2,
                paddingLeft: 2,
                paddingRight: 2,
                display: 'flex',
                justifyContent: 'space-between',
              }}
              variant="outlined">
              <Radio
                checked
                value="a"
                name="radio-buttons"
                inputProps={{ 'aria-label': 'A' }}
                sx={{
                  color: '#E10600',
                  '&.Mui-checked': {
                    color: '#E10600',
                  },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'center',
                  height: '68px',
                }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                  }}
                  fontSize={16}>
                  Servipag
                </Typography>
                <img height="54px" src={servipag} alt="Servipag" />
              </Box>
            </Card>
          </Grid>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Card
            sx={{
              boxShadow: '0 5px 25px 0 rgb(175 175 175 / 30%)',
              padding: '32px 42px',
              borderRadius: '16px',
            }}
            variant="outlined">
            <Box>
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                sx={{ height: '100%' }}>
                <Grid item container spacing={4}>
                  <Grid item>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: '#012D55',
                      }}
                      variant="h5"
                      fontSize={24}>
                      Detalle de pago
                    </Typography>
                  </Grid>
                  <Grid item sx={{ width: '100%' }}>
                    <Typography fontSize={16} fontWeight="600" lineHeight={2}>
                      Empresa:
                      <span style={{ float: 'right', color: '#012D55', fontWeight: '700' }}>
                        {clientName}
                      </span>
                    </Typography>
                    <Typography fontSize={16} fontWeight="600" lineHeight={2}>
                      Deuda por vencer:
                      <span style={{ float: 'right', fontWeight: '400' }}>
                        ${formatNumber(debt.toExpire)}
                      </span>
                    </Typography>
                    <Typography fontSize={16} fontWeight="600" lineHeight={2}>
                      Deuda vencida:
                      <span style={{ float: 'right', fontWeight: '400' }}>
                        ${formatNumber(debt.rawExpired)}
                      </span>
                    </Typography>
                    <Typography fontSize={16} lineHeight={2} fontWeight="600">
                      Intereses:
                      <span style={{ float: 'right', fontWeight: '400' }}>$0</span>
                    </Typography>
                    <Divider sx={{ margin: '23px 0px 17px 0px' }} />
                    <Typography fontSize={16} fontWeight="700">
                      Total a pagar:{' '}
                      <span style={{ float: 'right', color: '#012D55' }}>${debt.total}</span>
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item style={{ marginTop: '12px' }}>
                  <Button
                    disabled={submitting || debtToPay.debt === '' || !(debtToPay.total > 0)}
                    sx={{
                      width: '100%',
                      backgroundColor: '#E10600',
                      '&:hover': {
                        backgroundColor: '#bf0500',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#E10600',
                        color: 'white',
                        opacity: 0.5,
                      },
                      textTransform: 'none',
                    }}
                    color="primary"
                    variant="contained"
                    onClick={handlePayment}>
                    <Typography fontSize={15} fontWeight="700" color="white">
                      Pagar
                    </Typography>
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default Payment;
